import { Controller, type FieldValues, type Path } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
    appendAutocompleteIndicator,
    mergeAutocompleteAdornment
} from '../mergeAutocompleteAdornment';
import { indicatorRoomSx } from '../fieldEndButton';
import {
    recentKeyDownProps,
    showsRecommendedHint,
    type FormFieldBinding
} from './binding';
import { recommendedWarning } from './recommendedWarning';
import TabSuggestAutocomplete from '../TabSuggestAutocomplete';
import { hintsFilter } from '../hintsFilter';

export interface SuggestFieldOptions {
    /**
     * A short gloss shown after an option and searched along with it — for
     * vocabularies whose terms are not self-explaining. Omitted, the list is
     * the plain option strings.
     */
    hints?: Record<string, string>;
}

/**
 * A free-text field that offers what has been typed before: `freeSolo`, so the
 * options suggest without constraining. The value stays a plain string — this
 * is a text field with a memory, not a select.
 */
export function renderSuggestField<Values extends FieldValues>(
    binding: FormFieldBinding<Values>,
    name: Path<Values>,
    options: readonly string[],
    { hints }: SuggestFieldOptions = {}
) {
    return (
        <Controller
            name={name}
            control={binding.control}
            render={({ field, fieldState }) => {
                const recentTrigger = binding.recentTrigger?.(name);
                // MUI shows its clear X once something is typed (unless read
                // only) — the F2 button needs room beside it.
                const clearable =
                    !binding.readOnly && String(field.value ?? '') !== '';
                return (
                    <TabSuggestAutocomplete
                        freeSolo
                        sx={
                            recentTrigger
                                ? indicatorRoomSx(clearable ? 2 : 1)
                                : undefined
                        }
                        readOnly={binding.readOnly}
                        options={options as string[]}
                        inputValue={String(field.value ?? '')}
                        onInputChange={(_event, value) => field.onChange(value)}
                        onTake={field.onChange}
                        onBlur={field.onBlur}
                        {...recentKeyDownProps(binding, name)}
                        filterOptions={hints ? hintsFilter(hints) : undefined}
                        renderOption={
                            hints
                                ? (props, option) => (
                                      <li {...props} key={option}>
                                          {option}
                                          <Typography
                                              component="span"
                                              variant="body2"
                                              sx={{
                                                  color: 'text.secondary',
                                                  ml: 1
                                              }}
                                          >
                                              {hints[option] ?? ''}
                                          </Typography>
                                      </li>
                                  )
                                : undefined
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                inputRef={field.ref}
                                label={binding.intl.formatMessage({
                                    id: binding.labelId(name)
                                })}
                                error={!!fieldState.error}
                                helperText={
                                    fieldState.error
                                        ? binding.validationMessage(
                                              fieldState.error.message
                                          )
                                        : undefined
                                }
                                slotProps={{
                                    ...params.slotProps,
                                    input: {
                                        ...params.slotProps.input,
                                        // A no-op without a warning to merge, which
                                        // is why every form can take this path.
                                        endAdornment:
                                            mergeAutocompleteAdornment(
                                                appendAutocompleteIndicator(
                                                    params.slotProps.input
                                                        .endAdornment,
                                                    recentTrigger
                                                ),
                                                recommendedWarning(
                                                    binding.intl,
                                                    showsRecommendedHint(
                                                        binding,
                                                        name,
                                                        field.value
                                                    ),
                                                    true
                                                )
                                            )
                                    }
                                }}
                            />
                        )}
                    />
                );
            }}
        />
    );
}
